import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useBranding } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';
import Captcha from '@/components/Captcha';
import LanguageSelector from '@/components/LanguageSelector';

function SuperAdminLogin() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaData, setCaptchaData] = useState(null);
  const captchaRef = useRef(null);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  useEffect(() => {
    // Check if already authenticated
    checkAuth();
  }, []);
  
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      if (response.data.role === 'superadmin') {
        navigate('/superadmin/dashboard');
      }
    } catch (error) {
      // Not authenticated, stay on login page
    }
  };
  
  const handleCaptchaVerify = (data) => {
    setCaptchaVerified(true);
    setCaptchaData(data);
    setError('');
  };
  
  const handleCaptchaError = (errorMsg) => {
    setCaptchaVerified(false);
    setCaptchaData(null);
    setError(errorMsg);
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Verify math captcha if present
    if (captchaRef.current && !captchaVerified) {
      // Check if captcha input is filled
      const captchaInput = document.querySelector('[data-testid="captcha-answer-input"]');
      if (captchaInput && !captchaInput.value) {
        setError('Please solve the CAPTCHA');
        setLoading(false);
        return;
      }
      // For math captcha, verify the answer
      const expectedEl = document.querySelector('input[name="captcha_expected"]');
      const answerEl = document.querySelector('input[name="captcha_answer"]');
      if (expectedEl && answerEl) {
        if (parseInt(answerEl.value) !== parseInt(expectedEl.value)) {
          setError('Incorrect CAPTCHA answer. Please try again.');
          setLoading(false);
          return;
        }
      }
    }
    
    try {
      // Clear any leftover staff session so it doesn't override the new cookie
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_type');
      delete axios.defaults.headers.common['Authorization'];

      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      }, {
        withCredentials: true
      });
      
      if (response.data.role === 'superadmin') {
        if (response.data.session_token) {
          document.cookie = `session_token=${response.data.session_token}; path=/; SameSite=Lax; Max-Age=${30 * 24 * 3600}`;
        }
        navigate('/superadmin/dashboard', {
          state: {
            user: {
              user_id: response.data.user_id,
              email: response.data.email,
              name: response.data.name,
              role: response.data.role,
              session_token: response.data.session_token
            }
          }
        });
      } else {
        setError('Access denied. Super Admin role required.');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.platform_name} 
                className="h-16 object-contain"
              />
            ) : (
              <div className="p-4 bg-indigo-600 rounded-full">
                <Building2 className="w-12 h-12 text-white" />
              </div>
            )}
          </div>
          {/* Platform name hidden when logo is present */}
          {!branding.logo_url && (
            <h1 className="text-3xl font-bold text-gray-900">{branding.platform_name}</h1>
          )}
          <p className="text-gray-600 mt-2">{t('auth.superadmin_login')}</p>
        </div>
        
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>{t('nav.welcome')}</CardTitle>
                <CardDescription>{t('auth.superadmin_login')}</CardDescription>
              </div>
              <LanguageSelector compact />
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">{t('auth.email')}</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="admin@example.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">{t('auth.password')}</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>
              
              {/* CAPTCHA */}
              <div ref={captchaRef}>
                <Captcha 
                  loginType="superadmin"
                  onVerify={handleCaptchaVerify}
                  onError={handleCaptchaError}
                />
              </div>
              
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="error-message">
                  {error}
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? t('common.loading') : t('auth.login_btn')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SuperAdminLogin;
